import React, { useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  PaletteMode,
  Select,
  SelectChangeEvent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
  Alert,
  AlertTitle
} from '@mui/material';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import SiteForm from '../../components/hostComponents/SiteForm';
import SiteInformation from '../../components/hostComponents/SiteInformation';
import ManagementCalendar from '../../components/hostComponents/ManagementCalendar';
import ManagementCalendarOverview from '../../components/hostComponents/ManagementCalendarOverview';
import LoadingPage from '../../components/presentationalComponents/LoadingPage';
import AvailabilityForm from '../../components/hostComponents/AvailabilityForm';

import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import {
  defaultSite,
  deleteSelectedSite,
  fetchAllSites,
  getTotalOwnedSites,
  resetSiteErrorState,
  setSite
} from '../../store/Host/sitesManagement/siteSlice';

import { fetchAllSizes } from '../../store/Host/sitesManagement/sizesSlice';
import {
  fetchAllAvailabilities,
  resetAvailabilitiesErrorState
} from '../../store/Host/sitesManagement/availabilitySlice';

import { getReadableSiteType } from '../../utils/typeUtils';
import {Compound} from "../../services/restserver-openapi";
import { AvailabilityDetail, Site } from '../../services/restserver-openapi';
import { Main } from '../../layouts';
import Container from '../../components/Container';


// --- Type Definitions ---
type OverviewSectionProps = {
  selectedCompound: Compound;
  availabilities: AvailabilityDetail[];
};

type SiteDetailsSectionProps = {
  site: Site;
  sites: Site[];
  showSiteForm: boolean;
  isCreatingSite: boolean;
  onBack: () => void;
  handleSiteSelection: (event: SelectChangeEvent<string | number>) => void;
  availabilities: AvailabilityDetail[];
};

// --- Übersichtskomponente ---
const OverviewSection: React.FC<OverviewSectionProps> = ({ selectedCompound, availabilities }) => (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
        Kalenderübersicht für {selectedCompound.name}
      </Typography>
      <ManagementCalendarOverview availabilities={availabilities} />
    </Box>
);

// --- Ladezustand ---
const LoadingScreen: React.FC = () => (
    <div style={{ minHeight: '100vh' }}>
      <LoadingPage />
    </div>
);

const SiteDetailsSection: React.FC<SiteDetailsSectionProps> = ({
                                                                 site,
                                                                 sites,
                                                                 showSiteForm,
                                                                 isCreatingSite,
                                                                 onBack,
                                                                 handleSiteSelection,
                                                                 availabilities
                                                                }) => {
  return (
      <Box>
          {sites.length !== 0 ? (
              <Grid container spacing={2} justifyContent="space-between">
                {site?.siteId ? (
                    <Grid item xs={12}>
                      <Grid container my={1} justifyContent="flex-start">
                        <Grid item xs={12} sm={6} md={4}>
                          <FormControl fullWidth>
                            <InputLabel id="select-site">Unterkunft auswählen</InputLabel>
                            <Select
                                labelId="select-site"
                                id="select-site"
                                label="Unterkunft auswählen"
                                value={site.siteId || ''}
                                onChange={handleSiteSelection}
                            >
                              {sites.map((item) => (
                                  <MenuItem key={item.siteId} value={item.siteId}>
                                    {item.label}
                                  </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid>
                ) : (
                    <Grid item xs={12} md={4}>
                      <Typography variant="h4">Neue Unterkunft erstellen</Typography>
                    </Grid>
                )}

                {!isCreatingSite && (
                    <Grid item xs={12}>
                      <Typography variant="h5" mb={4}>
                        Verfügbarkeiten für den gewählten {getReadableSiteType(site)}
                      </Typography>
                      <AvailabilityForm availabilities={availabilities} />
                      <ManagementCalendar availabilities={availabilities} />
                    </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <Typography variant="h5">
                    Informationen zum gewählten {getReadableSiteType(site)}
                  </Typography>
                </Grid>

                {showSiteForm ? (
                    <Grid item xs={12} md={3} />
                ) : (
                    <Grid item xs={12} sm={6} />
                )}

                <Grid item xs={12}>
                  {showSiteForm ? (
                      <>
                        <SiteForm onBack={onBack} />
                        <Box mt={2}>
                          <Button variant="outlined" color="secondary" onClick={onBack}>
                            Abbrechen
                          </Button>
                        </Box>
                      </>
                  ) : (
                      <SiteInformation />
                  )}
                </Grid>
              </Grid>
          ) : (
              <Grid container my={3} spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    Keine Unterkünfte vorhanden. Erstelle Deine erste Unterkunft!
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  {showSiteForm && <SiteForm onBack={onBack} />}
                </Grid>
              </Grid>
          )}
      </Box>
  );
};

const SitesManagement = () => {
  const theme = useTheme();
  const sites = useAppSelector(state => state.site.sites);
  const availabilities = useAppSelector(state => state.availability.availabilities);
  const site = useAppSelector(state => state.site.site);
  const [showSiteForm, setShowSiteForm] = useState(false);
  const dispatch = useAppDispatch();
  const [isCreatingSite, setIsCreatingSite] = useState(false);
  const loading = useAppSelector(state => state.site.loading);
  const hostId = useAppSelector(state => state.login.userSpecificationId);
  const selectedCompound = useAppSelector(state => state.compounds.selectedCompound);
  const compoundId = selectedCompound?.compoundId;
  const [overview, setOverview] = useState(false);

  const compoundBelongsToHost = Number(selectedCompound?.hostId) === Number(hostId);
  const totalOwnedSites = useAppSelector(state => state.site.totalOwnedSites);
  const userEntitlements = useAppSelector(state => state.user.entitlements);

  const canAddMoreSites = useMemo(() => {
    const entitlement = userEntitlements.find(e => e.featureId === 'hostownednumberofsites')?.value;
    return entitlement === 'Unlimited' || totalOwnedSites < Number(entitlement);
  }, [userEntitlements, totalOwnedSites]);

  useEffect(() => {
    if (compoundId) {
      dispatch(fetchAllSites(compoundId)).unwrap().then((payload) => {
        if (payload) {
          dispatch(setSite(payload[0]));
          dispatch(fetchAllSizes());
          dispatch(getTotalOwnedSites());
        }
      });
    }
  }, [dispatch, compoundId]);

  useEffect(() => {
    dispatch(resetSiteErrorState());
    dispatch(resetAvailabilitiesErrorState());
  }, [dispatch]);

  useEffect(() => {
    if (site?.offer?.offerId) {
      dispatch(fetchAllAvailabilities(site.offer.offerId));
    }
  }, [dispatch, site]);

  const handleAddSite = async () => {
    setOverview(false);
    setIsCreatingSite(true);
    if (compoundId) {
      dispatch(setSite({ compoundId, ...defaultSite, offer: { ...defaultSite.offer, hostId } }));
    }
    setShowSiteForm(true);
  };

  const handleEditSite = () => {
    setOverview(false);
    setIsCreatingSite(false);
    setShowSiteForm(true);
  };

  const handleDeleteSite = async () => {
    if (site?.siteId && compoundId) {
      await dispatch(deleteSelectedSite(site.siteId));
      const payload = await dispatch(fetchAllSites(compoundId)).unwrap();
      if (payload && payload.length > 0) {
        dispatch(setSite(payload[0]));
      }
      await dispatch(getTotalOwnedSites());
    }
    handleCloseDeleteDialog();
  };

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const handleOpenDeleteDialog = () => setOpenDeleteDialog(true);
  const handleCloseDeleteDialog = () => setOpenDeleteDialog(false);

  const DeleteDialog = () => (
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Unterkunft löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchtest du die Unterkunft wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} variant="outlined">Abbrechen</Button>
          <Button onClick={handleDeleteSite} color="primary" autoFocus variant="contained">Löschen</Button>
        </DialogActions>
      </Dialog>
  );

  const handleSiteSelection = (event: SelectChangeEvent<string | number>) => {
    if (showSiteForm) setShowSiteForm(false);
    const id = Number(event.target.value);
    const found = sites.find((item) => item.siteId === id);
    if (found) dispatch(setSite(found));
  };

  const onBack = async () => {
    setShowSiteForm(false);
    setIsCreatingSite(false);
    if (compoundId) {
      const payload = await dispatch(fetchAllSites(compoundId)).unwrap();
      if (payload && payload.length > 0 && (!site || !payload.some(item => item.siteId === site.siteId))) {
        dispatch(setSite(payload[0]));
      }
    }
  };

  const HeaderContent = () => (
      <Box>
        <Typography
            sx={{
              textTransform: 'uppercase',
              fontWeight: 500,
              letterSpacing: 1,
            }}
            gutterBottom
            color={'text.secondary'}
        >
          Gastgeber
        </Typography>
        <Box marginBottom={2}>
          <Typography
              variant="h3"
              color="text.primary"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
              }}
          >
            Hier verwaltest Du{' '}
            <Typography
                color={'text.primary'}
                component={'span'}
                variant={'inherit'}
                sx={{
                  background: `linear-gradient(180deg, transparent 82%, ${alpha(
                      theme.palette.mode === 'light' as PaletteMode ? theme.palette.secondary.main : theme.palette.primary.main,
                      0.3,
                  )} 0%)`,
                }}
            >
              Unterkünfte und Verfügbarkeiten
            </Typography>
          </Typography>
        </Box>
        <Box marginBottom={3}>
          <Typography variant="h6" component="p" color="text.secondary">
            Wähle eine Unterkunft, pflege Verfügbarkeiten und bearbeite die Daten Deiner Unterkunft.
          </Typography>
        </Box>
      </Box>
  );

  const ActionRow = (props: {
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
    active?: boolean;
    disabled?: boolean;
    tooltip?: string;
    onClick?: () => void;
  }) => {
    const { icon, title, subtitle, active, disabled, tooltip, onClick } = props;

    const content = (
        <ListItemButton
            disabled={disabled}
            onClick={onClick}
            sx={{
              borderRadius: 2,
              px: 1.25,
              py: 1,
              gap: 0.5,
              '&.Mui-disabled': { opacity: 0.55 },
              ...(active
                  ? {
                    bgcolor: 'action.selected',
                    '&:hover': { bgcolor: 'action.selected' },
                  }
                  : {}),
            }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: active ? 'primary.main' : 'text.secondary' }}>
            {icon}
          </ListItemIcon>

          <ListItemText
              primary={<Typography sx={{ fontWeight: 800 }} noWrap>{title}</Typography>}
              secondary={subtitle}
              secondaryTypographyProps={{ color: 'text.secondary' }}
          />
        </ListItemButton>
    );

    return tooltip ? (
        <Tooltip title={tooltip} arrow>
          <Box component="span">{content}</Box>
        </Tooltip>
    ) : (
        content
    );
  };

  const canEditOrDeleteSite = compoundBelongsToHost && Boolean(site?.siteId) && !showSiteForm;
  const addSiteDisabled = !compoundBelongsToHost || !canAddMoreSites || !compoundId;

  if (loading) return <LoadingScreen />;

  return (
      <Main>
        <Container>
          <HeaderContent />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2 }}>
            <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 4,
                  border: 0,
                  boxShadow: 'none',
                  bgcolor: 'background.paper',
                }}
            >
              <Typography variant="overline" color="text.secondary">
                Aktionen
              </Typography>
              <List disablePadding>
                <ActionRow
                    icon={<CalendarMonthIcon />}
                    title="Kalenderübersicht"
                    subtitle="Alle Verfügbarkeiten im Überblick"
                    active={overview}
                    disabled={!compoundId}
                    onClick={() => setOverview(true)}
                />

                <ActionRow
                    icon={<EditIcon />}
                    title="Detailansicht"
                    subtitle="Verfügbarkeiten und Unterkunft bearbeiten"
                    active={!overview}
                    disabled={!compoundId}
                    onClick={() => setOverview(false)}
                />

                <ActionRow
                    icon={<AddCircleIcon />}
                    title="Unterkunft hinzufügen"
                    subtitle="Neue Unterkunft zum Betrieb anlegen"
                    disabled={addSiteDisabled || showSiteForm}
                    tooltip={!compoundBelongsToHost
                        ? 'Keine Berechtigung zum Hinzufügen.'
                        : !canAddMoreSites
                            ? 'Limit erreicht. Add-on nötig.'
                            : undefined}
                    onClick={handleAddSite}
                />

                <ActionRow
                    icon={<EditIcon />}
                    title="Unterkunft bearbeiten"
                    subtitle="Daten der ausgewählten Unterkunft anpassen"
                    disabled={!canEditOrDeleteSite}
                    tooltip={!compoundBelongsToHost
                        ? 'Keine Bearbeitungsrechte.'
                        : !site?.siteId
                            ? 'Bitte zuerst eine Unterkunft auswählen.'
                            : showSiteForm
                                ? 'Bearbeitung ist bereits geöffnet.'
                                : undefined}
                    onClick={handleEditSite}
                />

                <ActionRow
                    icon={<DeleteIcon />}
                    title="Unterkunft löschen"
                    subtitle="Ausgewählte Unterkunft entfernen"
                    disabled={!canEditOrDeleteSite}
                    tooltip={!compoundBelongsToHost
                        ? 'Keine Löschrechte.'
                        : !site?.siteId
                            ? 'Bitte zuerst eine Unterkunft auswählen.'
                            : showSiteForm
                                ? 'Während der Bearbeitung deaktiviert.'
                                : undefined}
                    onClick={handleOpenDeleteDialog}
                />

                {showSiteForm && (
                    <ActionRow
                        icon={<CancelIcon />}
                        title="Bearbeitung beenden"
                        subtitle="Zurück zur Detailansicht"
                        active
                        onClick={onBack}
                    />
                )}
              </List>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  p: { xs: 2, md: 3 },
                  border: 0,
                  boxShadow: 'none',
                  bgcolor: 'background.paper',
                }}
            >
              <Typography variant="overline" color="text.secondary">
                Verfügbarkeiten
              </Typography>

              {!compoundId ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <AlertTitle>Du musst einen Betrieb auswählen</AlertTitle>
                    Um Verfügbarkeiten zu managen, wähle zuerst einen Betrieb über den Wechsler oben rechts aus.
                  </Alert>
              ) : overview ? (
                  <OverviewSection
                      selectedCompound={selectedCompound}
                      availabilities={availabilities}
                  />
              ) : (
                  <SiteDetailsSection
                      site={site}
                      sites={sites}
                      showSiteForm={showSiteForm}
                      isCreatingSite={isCreatingSite}
                      onBack={onBack}
                      handleSiteSelection={handleSiteSelection}
                      availabilities={availabilities}
                  />
              )}
            </Paper>
          </Box>
          <DeleteDialog />
        </Container>
      </Main>
  );
};

export default SitesManagement;
